import { writable } from "svelte/store";
import { getContext } from "svelte";
import {
  keyUpDown,
  keydown,
  listen,
  setAttribute,
  trackAttribute,
  unify,
} from "@lernetz/svelte-common/actions";
import type { Overlay } from "./overlay";

export type Menu = ReturnType<typeof createDropdownMenu>;
export type RadioGroup = ReturnType<typeof createDropdownRadioGroup>;

export const DropdownContext = Symbol("DropdownContext");
export const DropdownRadioGroupContext = Symbol("DropdownRadioGroupContext");

export const createDropdownMenu = (overlay: Overlay) => {
  const root = (node: HTMLElement) => {
    let focusedIndex = -1;
    let menuItems = [];

    const updateMenuItems = () => {
      menuItems = Array.from(
        node.querySelectorAll<HTMLElement>(
          "[role='menuitem']:not([aria-disabled='true']), [role='menuitemradio']:not([aria-disabled='true']), [role='menuitemcheckbox']:not([aria-disabled='true'])",
        ),
      );
    };

    const handleArrowKeys = (direction: "up" | "down") => (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (direction === "up") {
        focusedIndex = focusedIndex <= 0 ? menuItems.length - 1 : focusedIndex - 1;
      } else {
        focusedIndex = focusedIndex >= menuItems.length - 1 ? 0 : focusedIndex + 1;
      }

      menuItems[focusedIndex].focus();
    };

    setAttribute(node, {
      role: "menu",
      "aria-orientation": "vertical",
      tabindex: "-1",
    });

    updateMenuItems();

    const observeChanges = new MutationObserver(updateMenuItems);

    observeChanges.observe(node, {
      childList: true,
      subtree: true,
    });

    return keyUpDown(node.ownerDocument, {
      up: handleArrowKeys("up"),
      down: handleArrowKeys("down"),
    });
  };

  const separator = (node: HTMLElement) => {
    setAttribute(node, {
      role: "separator",
      "aria-orientation": "horizontal",
      tabindex: "-1",
    });
  };

  const menuItem = (
    node: HTMLElement,
    options?: {
      closeOnSelect?: boolean;
      disabled?: boolean;
      onSelect?: () => void;
    },
  ) => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        node.click();
      }
    };

    const handleClick = (event: MouseEvent) => {
      if (options?.disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (options?.onSelect) {
        event.preventDefault();
        event.stopPropagation();

        options?.onSelect();
      }
      if (options?.closeOnSelect) overlay.close();
    };

    setAttribute(node, {
      role: "menuitem",
      tabindex: "-1",
      "aria-disabled": options?.disabled ? "true" : "false",
    });

    return unify([
      keydown(node, handleKeyDown),
      listen(node, { type: "click", handler: handleClick }),
    ]);
  };

  const checkboxItem = (
    node: HTMLElement,
    options: {
      closeOnSelect?: boolean;
      checked: boolean;
      disabled?: boolean;
      onChange: (checked: boolean) => void;
    },
  ) => {
    let checked = options.checked;

    const handleChange = (event: Event) => {
      if (options.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      options.onChange(!checked);

      if (options.closeOnSelect) overlay.close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        handleChange(event);
      }
    };

    const handleClick = (event: MouseEvent) => {
      handleChange(event);
    };

    setAttribute(node, {
      role: "menuitemcheckbox",
      tabindex: "-1",
      "aria-checked": checked ? "true" : "false",
      "aria-disabled": options.disabled ? "true" : "false",
    });

    return {
      ...unify([
        keydown(node, handleKeyDown),
        listen(node, { type: "click", handler: handleClick }),
      ]),
      update(options: { checked: boolean; disabled?: boolean }) {
        checked = options.checked;
        node.setAttribute("aria-checked", options.checked ? "true" : "false");
        node.setAttribute("aria-disabled", options.disabled ? "true" : "false");
      },
    };
  };

  return {
    root,
    item: menuItem,
    checkbox: checkboxItem,
    separator,
  };
};

export const createDropdownRadioGroup = (initialValue: string) => {
  const { overlay } = getContext<{ overlay: Overlay }>(DropdownContext);
  const internalValue = writable<string>(null);

  const root = (node: HTMLElement) => {
    node.setAttribute("role", "group");
  };

  const radioGroupItem = (
    node: HTMLElement,
    options: {
      value: string;
      closeOnSelect?: boolean;
      disabled?: boolean;
    },
  ) => {
    const handleChange = (event: Event) => {
      if (options.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      internalValue.set(options.value);

      if (options.closeOnSelect) overlay.close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        handleChange(event);
      }
    };

    const handleClick = (event: MouseEvent) => {
      handleChange(event);
    };

    setAttribute(node, {
      role: "menuitemradio",
      tabindex: "-1",
      "aria-disabled": options.disabled ? "true" : "false",
    });

    trackAttribute(node, {
      attribute: "aria-checked",
      store: internalValue,
      value: (newValue) => (newValue === options.value ? "true" : "false"),
    });

    return {
      ...unify([
        keydown(node, handleKeyDown),
        listen(node, { type: "click", handler: handleClick }),
      ]),
      update(options: { disabled?: boolean }) {
        node.setAttribute("aria-disabled", options.disabled ? "true" : "false");
      },
    };
  };

  return {
    root,
    item: radioGroupItem,
    value: internalValue,
  };
};
